/**
 * Lightweight filter data for query generation.
 * Constructs minimal filter objects without importing filter definition files,
 * avoiding transitive deps on smart-user-picker, @atlaskit/select, react-dom etc.
 *
 *
 */
import type {
	AllFilterStates,
	MinimalFilterValue,
} from '../../../../common/constants/filters/types';
import {
	all1pProductKeys,
	all3pProductKeys,
	ProductKeys,
	ProductKeys3P,
	type ProductKeys1P,
} from '../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../common/utils/filters/get-products-3p';
import { createBooleanFilter } from '../../../filters/boolean-filter/create-boolean-filter';
import { createMultiSelectFilter } from '../../../filters/multi-select-filter';
import { createSingleSelectFilter } from '../../../filters/single-select-filter';

export type FilterDataEntry = {
	id: string;
	type: string;
	queryParams: {
		key: string;
		getState: (queryParams: Record<string, string[]>, cloudId: string) => AllFilterStates;
	};
	products: string[];
	universal: boolean;
};

// Helper product arrays
const confluenceProducts: ProductKeys1P[] = [ProductKeys.Confluence];
const jiraProducts: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];
const assetsProducts: ProductKeys1P[] = [ProductKeys.Assets];
const mercuryProducts: ProductKeys1P[] = [ProductKeys.Focus];
const talentProducts: ProductKeys1P[] = [ProductKeys.Talent];
const contributorProducts: ProductKeys1P[] = all1pProductKeys.filter(
	(key) => key !== ProductKeys.Trello && key !== ProductKeys.Focus && key !== ProductKeys.Talent,
);
const contributor3P = all3pProductKeys.filter((key) => key !== ProductKeys3P.Figma);

export const allFilterData: Record<string, MinimalFilterValue> = {
	lastUpdated: createMultiSelectFilter({
		id: 'lastUpdated',
		products: [...all1pProductKeys, ...all3pProductKeys],
		alwaysVisible: false,
		universal: true,
		queryParams: { key: 'lastModified' },
	}),
	contributor: createMultiSelectFilter({
		id: 'contributor',
		products: [...contributorProducts, ...contributor3P],
		alwaysVisible: true,
		universal: true,
		queryParams: { key: 'contributors' },
	}),
	space: createMultiSelectFilter({
		id: 'space',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('space')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'spaces' },
	}),
	watchedByMe: createBooleanFilter({
		id: 'watchedByMe',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraDC,
			ProductKeys.JsmDC,
			ProductKeys.JiraServiceManagement,
			ProductKeys.JiraProductDiscovery,
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jWatchedByMe' },
	}),
	verified: createBooleanFilter({
		id: 'verified',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('verified')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'verified' },
	}),
	assignee: createMultiSelectFilter({
		id: 'assignee',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('assignee')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jAssignee' },
	}),
	reporter: createMultiSelectFilter({
		id: 'reporter',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('reporter')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jReporter' },
	}),
	commentedByMe: createBooleanFilter({
		id: 'commentedByMe',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraDC,
			ProductKeys.JsmDC,
			ProductKeys.JiraServiceManagement,
			ProductKeys.JiraProductDiscovery,
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'jCommentedByMe' },
	}),
	project: createMultiSelectFilter({
		id: 'project',
		products: [...jiraProducts, ...getProducts3PFromConfigForFilter('project')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'jProject' },
	}),
	universalTypeFilter: createSingleSelectFilter({
		id: 'universalTypeFilter',
		products: [...all1pProductKeys, ...all3pProductKeys],
		alwaysVisible: true,
		universal: true,
		queryParams: { key: 'type' },
	}),
	content: createMultiSelectFilter({
		id: 'content',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('content')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'ancestor' },
	}),
	onlyTitleMatch: createBooleanFilter({
		id: 'onlyTitleMatch',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('onlyTitleMatch')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'titleMatch' },
	}),
	owner: createMultiSelectFilter({
		id: 'owner',
		products: [
			ProductKeys.Confluence,
			ProductKeys.Focus,
			...getProducts3PFromConfigForFilter('owner'),
		],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'owners' },
	}),
	createdBy: createMultiSelectFilter({
		id: 'createdBy',
		products: [...getProducts3PFromConfigForFilter('createdBy')],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'createdBy' },
	}),
	label: createMultiSelectFilter({
		id: 'label',
		products: [
			...confluenceProducts,
			...jiraProducts,
			...getProducts3PFromConfigForFilter('label'),
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'labels' },
	}),
	focusAreaType: createMultiSelectFilter({
		id: 'focusAreaType',
		products: [...mercuryProducts],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'focusAreaTypes' },
	}),
	focusAreaUnder: createMultiSelectFilter({
		id: 'focusAreaUnder',
		products: [...mercuryProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'focusAreaUnder' },
	}),
	pageStatus: createMultiSelectFilter({
		id: 'pageStatus',
		products: [...confluenceProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'pageStatus' },
	}),
	archived: createBooleanFilter({
		id: 'archived',
		products: [...confluenceProducts, ...getProducts3PFromConfigForFilter('archived')],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'archived' },
	}),
	lastUpdatedBy: createMultiSelectFilter({
		id: 'lastUpdatedBy',
		products: [ProductKeys.Salesforce, ProductKeys.Hubspot],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'lastUpdatedBy' },
	}),
	statusFilter: createMultiSelectFilter({
		id: 'statusFilter',
		products: [
			ProductKeys.Jira,
			ProductKeys.JiraDC,
			ProductKeys.JsmDC,
			ProductKeys.JiraServiceManagement,
			ProductKeys.JiraProductDiscovery,
		],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'status' },
	}),
	compassArchived: createBooleanFilter({
		id: 'compassArchived',
		products: [ProductKeys.Compass],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'compassArchived' },
	}),
	assetsSchema: createMultiSelectFilter({
		id: 'assetsSchema',
		products: [...assetsProducts],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'assetsSchema' },
	}),
	assetsType: createMultiSelectFilter({
		id: 'assetsType',
		products: [...assetsProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'assetsType' },
	}),
	positionStatus: createMultiSelectFilter({
		id: 'positionStatus',
		products: [...talentProducts],
		alwaysVisible: true,
		universal: false,
		queryParams: { key: 'positionStatuses' },
	}),
	positionEmploymentType: createMultiSelectFilter({
		id: 'positionEmploymentType',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionEmploymentTypes' },
	}),
	positionJobFamily: createMultiSelectFilter({
		id: 'positionJobFamily',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionJobFamilies' },
	}),
	positionTeam: createMultiSelectFilter({
		id: 'positionTeam',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionTeams' },
	}),
	positionFocusArea: createMultiSelectFilter({
		id: 'positionFocusArea',
		products: [...talentProducts],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'positionFocusAreas' },
	}),
	opportunityStatus: createMultiSelectFilter({
		id: 'opportunityStatus',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'opportunityStatus' },
	}),
	sort: createSingleSelectFilter({
		id: 'sort',
		products: [...all1pProductKeys, ...all3pProductKeys],
		alwaysVisible: false,
		universal: true,
		queryParams: { key: 'sort' },
	}),
	salesforceStage: createMultiSelectFilter({
		id: 'salesforceStage',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'salesforceStages' },
	}),
	closeDate: createSingleSelectFilter({
		id: 'closeDate',
		products: [ProductKeys.Salesforce],
		alwaysVisible: false,
		universal: false,
		queryParams: { key: 'closeDate' },
	}),
};
